import {
    createContext,
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useContext as useReactContext,
    useRef
} from "react"
import type { BaseSyntheticEvent, FC, ForwardedRef, Context as ReactContext, ReactElement, RefObject } from "react"

import isNull from "lodash-es/isNull"
import map from "lodash-es/map"
import reject from "lodash-es/reject"
import { type FieldErrors, type UseFormReturn, useForm } from "react-hook-form"
import { useUnmount, useUpdateEffect } from "react-use"

import { type IUseTranslation, useTranslation } from "@/hooks"

import { FreeResponses as Component } from "./free-responses.component"
import { getFreeResponsesFormSchemaResolver as getFormResolver } from "./free-responses.schema"
import {
    EFreeResponsesTheme as ETheme,
    type TFreeResponsesConfig as TConfig,
    type TFreeResponsesEntityRef as TEntityRef,
    type TFreeResponsesFormData as TForm,
    type TFreeResponsesFormQuestion as TFormQuestion,
    type TFreeResponsesQuestion as TQuestion,
    type TFreeResponsesSubmittedQuestion as TQuestionSubmitted
} from "./free-responses.types"

type TContextType = {
    form: {
        methods: Partial<UseFormReturn<TForm>>
        errors: FieldErrors<TForm>
        isSubmitting: boolean
        isValid: boolean
        isRequired: boolean
        handleSubmit(e: BaseSyntheticEvent): Promise<void>
    }
    onQuestionComplete(question: TQuestionSubmitted): Promise<void>
    title: string
    theme: ETheme
    questionList: TQuestion[]
    isError: boolean
    withManualSubmit: boolean
    submitButtonText: string | undefined
}

const Context: ReactContext<TContextType> = createContext<TContextType | undefined>(undefined)

function useContext(): TContextType {
    const context: TContextType = useReactContext(Context)

    if (!context) throw new Error("useFreeResponses must be used within a FreeResponsesProvider")

    return context
}

const Provider: FC<TConfig> = forwardRef(
    (
        {
            title,
            theme,
            questions,
            isRequired,
            isError,
            onAllQuestionsComplete,
            withManualSubmit = false,
            wrapperClassName = undefined,
            manualSubmitButtonText: submitButtonText = undefined
        }: TConfig,
        ref: ForwardedRef<TEntityRef>
    ): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        const formResolver: ReturnType<typeof getFormResolver> = useMemo(
            (): ReturnType<typeof getFormResolver> => getFormResolver(questions, t),
            [questions, t]
        )

        const {
            control: formControl,
            trigger: triggerForm,
            handleSubmit: handleFormSubmit,
            reset: resetForm,
            formState
        }: UseFormReturn<TForm> = useForm<TForm>({
            mode: "onChange",
            reValidateMode: "onChange",
            resolver: formResolver,
            shouldFocusError: true,
            defaultValues: {
                questionList: map<TQuestion, TFormQuestion>(
                    questions,
                    (q: TQuestion, idx: number): TFormQuestion => ({
                        idx,
                        id: q.initialState.id,
                        question: q.initialState.question,
                        answer: isNull(q.submittedState) ? String("") : q.submittedState.answer
                    })
                )
            }
        })

        const submittedQuestions: RefObject<TQuestionSubmitted[]> = useRef<TQuestionSubmitted[]>(
            map<TQuestion, TQuestionSubmitted>(
                questions,
                (q: TQuestion): TQuestionSubmitted => ({
                    answer: isNull(q.submittedState) ? String("") : q.submittedState.answer,
                    id: q.initialState.id,
                    question: q.initialState.question
                })
            )
        )

        const handleSubmit: (e: BaseSyntheticEvent) => Promise<void> = useMemo(
            (): ((e: BaseSyntheticEvent) => Promise<void>) =>
                handleFormSubmit(async (): Promise<void> => {
                    await onAllQuestionsComplete?.(submittedQuestions.current)
                }),
            [handleFormSubmit, onAllQuestionsComplete]
        )

        useImperativeHandle(ref, (): TEntityRef => ({ isCompleted: formState.isValid }), [formState.isValid])

        useUpdateEffect((): void => {
            if (withManualSubmit) return

            if (formState.isValid)
                triggerForm().then(
                    async (isValid: boolean): Promise<void> =>
                        isValid ? onAllQuestionsComplete?.(submittedQuestions.current) : void 0
                )
        }, [formState.isValid, triggerForm, onAllQuestionsComplete, withManualSubmit])

        useUnmount((): void => {
            resetForm()
        })

        const handleQuestionComplete: (question: TQuestionSubmitted) => Promise<void> = useCallback(
            async (question: TQuestionSubmitted): Promise<void> => {
                submittedQuestions.current = [
                    ...reject(submittedQuestions.current, { id: question.id }),
                    { ...question }
                ]
            },
            []
        )

        const providerValue: TContextType = useMemo(
            (): TContextType => ({
                form: {
                    errors: formState.errors,
                    methods: { control: formControl },
                    isRequired,
                    isSubmitting: formState.isSubmitting,
                    isValid: formState.isValid,
                    handleSubmit
                },
                onQuestionComplete: handleQuestionComplete,
                questionList: questions,
                title,
                theme,
                withManualSubmit,
                submitButtonText,
                isError
            }),
            [
                formState.errors,
                formControl,
                questions,
                title,
                theme,
                withManualSubmit,
                isRequired,
                formState.isSubmitting,
                formState.isValid,
                handleQuestionComplete,
                handleSubmit,
                submitButtonText,
                isError
            ]
        )

        return (
            <Context.Provider value={providerValue}>
                <Component wrapperClassName={wrapperClassName} />
            </Context.Provider>
        )
    }
)

Provider.displayName = "FreeResponsesProvider"

export { Provider as FreeResponsesProvider }
export { useContext as useFreeResponsesContext }
